'use client';

import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { Button } from "@/components/ui/Button";
import { useLanguage } from "@/providers/LanguageContext";
import styles from "../app/page.module.css";

interface FeaturesSectionProps {
  onContactClick: () => void;
}

export function FeaturesSection({ onContactClick }: FeaturesSectionProps) {
  const { language } = useLanguage();
  const router = useRouter();

  return (
    <>
      {/* Why Choose ALI Section */}
      <div id="section-purpose" className={styles['features-why-section']}>
        <div className={styles['features-container']}>
          <h2 className={styles['features-title']}>
            {language === "es" ? "¿Por qué elegir ALI?" : "Why Choose ALI?"}
          </h2>
          <p className={styles['features-text']}>
            {language === "es"
              ? "En Applied Language Institute, entendemos que el aprendizaje de idiomas debe ser atractivo y efectivo, especialmente para los niños. Nuestro centro de idiomas ofrece clases de inglés diseñadas para crear hablantes confiados y fluidos a través del aprendizaje interactivo y la atención personalizada. Ubicado en el corazón de Oxapampa, nuestro centro de idiomas proporciona un ambiente conveniente y de apoyo para que niños y adultos dominen el inglés."
              : "At Applied Language Institute, we understand that language learning needs to be engaging and effective, especially for children. Our language center offers English classes designed to create confident, fluent speakers through interactive learning and personalized attention. Located in the heart of Oxapampa, our language center provides a convenient and supportive environment for children and adults to master English."}
          </p>

          <h2 className={styles['features-title-top-margin']}>
            {language === "es" ? "La diferencia ALI" : "The ALI Difference"}
          </h2>
          <p className={styles['class-section-text-bottom']}>
            {language === "es"
              ? "¡Sin aburridas clases en video! La forma ALI proporciona clases totalmente dirigidas por instructores e interactivas EN VIVO con maestros calificados con certificaciones internacionales. Ofrecemos materiales apropiados para cada edad con tareas y evaluaciones reales. Nuestros profesores tienen certificaciones internacionales y una amplia formación que llevará a cada estudiante a su máximo potencial, ya sea preparándose para exámenes internacionales de inglés o aprendiendo inglés general."
              : "No boring video lectures! The ALI way provides fully instructor-led and LIVE interactive classes with qualified, internationally certified teachers. We offer age-appropriate materials with real assignments and evaluations. Our teachers have international certifications and extensive training that will bring each student up to his or her full potential, whether preparing for international English exams or learning general English."}
          </p>
        </div>
      </div>

      {/* Core Classes Section */}
      <div id="section-classes" className={styles['features-classes-section']}>
        <div className={styles['features-container']}>
          {/* Kids English Class */}
          <div className={`${styles['class-item']}`}>
            <div className={`${styles['class-image']}`}>
              <Image 
                src="/images/icons/english-for-kids.svg" 
                alt="Kids learning English"
                width={300}
                height={330}
                className={`${styles['bouncing-image-slow']} ${styles['class-svg-image']}`}
              />
            </div>
            <div className={`${styles['class-content']}`}>
              <h2 className={styles['class-section-title']}>
                {language === "es" ? "Inglés para Niños" : "English for Kids"}
              </h2>
              <p className={styles['class-section-text']}>
                {language === "es"
                  ? "¡Regala a tu hijo el don del idioma! Convenientemente ubicado a dos cuadras del centro de la ciudad de Oxapampa, Applied Language Institute ofrece clases de inglés atractivas, divertidas y efectivas diseñadas específicamente para niños."
                  : "Give your child the gift of language! Conveniently located two blocks from the Oxapampa city center, Applied Language Institute offers engaging, fun, and effective English classes specifically designed for children."}
              </p>
              <p className={styles['class-section-text']}>
                {language === "es"
                  ? "Nuestro plan de estudios amigable para niños utiliza juegos, actividades, canciones e historias para hacer que el aprendizaje sea natural y agradable. Las clases se ofrecen después de la escuela y los fines de semana para adaptarse al horario de su familia. Los niños desarrollan confianza al hablar mientras construyen una base sólida en habilidades de lectura, escritura y comprensión auditiva."
                  : "Our kid-friendly curriculum uses games, activities, songs, and stories to make learning natural and enjoyable. Classes are offered after school and on weekends to fit your family's schedule. Children develop confidence in speaking while building a strong foundation in reading, writing, and listening skills."}
              </p>
              <p className={styles['class-section-text-bottom']}>
                {language === "es"
                  ? "Las clases se dividen por grupos de edad: 5-7 años, 8-10 años y 11-13 años, asegurando que los materiales y métodos de enseñanza sean apropiados para cada etapa de desarrollo."
                  : "Classes are divided by age groups: 5-7 years, 8-10 years, and 11-13 years, ensuring that materials and teaching methods are appropriate for each developmental stage."}
              </p>
              <Button
                colorScheme="purple"
                onClick={onContactClick}
              >
                {language === "es" ? "Contacto" : "Contact"}
              </Button>
            </div>
          </div>

          {/* International Exam Preparation */}
          <div className={`${styles['class-item']} ${styles['reverse']}`}>
            <div className={`${styles['class-image']}`}>
              <Image 
                src="/images/icons/c2-proficiency-preparation.svg" 
                alt="International Exam Preparation"
                width={280}
                height={320}
                className={`${styles['bouncing-image-slow']} ${styles['class-svg-image-exam']}`}
              />
            </div>
            <div className={`${styles['class-content']}`}>
              <div className={styles['exam-title-container']}>
                <h2 className={styles['exam-title']}>
                  {language === "es" ? "Preparación para Exámenes Internacionales" : "International Exam Preparation"}
                </h2>
              </div>
              <p className={styles['class-section-text']}>
                {language === "es"
                  ? "Ofrecemos cursos especializados para ayudar a los estudiantes a obtener calificaciones de inglés reconocidas internacionalmente. Nuestros programas de preparación cubren una amplia gama de exámenes internacionales, incluyendo:"
                  : "We offer specialized courses to help students achieve internationally recognized English qualifications. Our exam preparation programs cover a wide range of international exams including:"}
              </p>
              <ul className={styles['exam-list']}>
                <li>LanguageCert International ESOL (A1–C2)</li>
                <li>Pearson PTE General (A1–C2)</li>
                <li>B2 First (FCE), C1 Advanced (CAE), C2 Proficiency (CPE)</li>
                <li>Pre A1 Starters, A1 Movers, A2 Flyers (for young learners)</li>
              </ul>
              <p className={styles['class-section-text-bottom']}>
                {language === "es"
                  ? "Nuestra preparación intensiva y de alta calidad para exámenes te da la mejor oportunidad de éxito en tu primer intento. Proporcionamos cursos completos a precios asequibles con resultados probados en los principales exámenes internacionales de inglés."
                  : "Our intensive, high-quality exam preparation gives you the best chance of success on your first attempt. We provide complete courses at affordable prices with proven results across all major international English exams."}
              </p>
              <Button
                colorScheme="purple"
                onClick={onContactClick}
              >
                {language === "es" ? "Contacto" : "Contact"}
              </Button>
            </div>
          </div>

          {/* General English */}
          <div className={`${styles['class-item']}`}>
            <div className={`${styles['class-image']}`}>
              <Image 
                src="/images/icons/english-for-adults.svg" 
                alt="Adults learning English"
                width={280}
                height={320}
                className={`${styles['bouncing-image-slow']} ${styles['class-svg-image-exam']}`}
              />
            </div>
            <div className={`${styles['class-content']}`}>
              <h2 className={styles['class-section-title']}>
                {language === "es" ? "Inglés General para Adultos" : "General English for Adults"}
              </h2>
              <p className={styles['class-section-text']}>
                {language === "es"
                  ? "Ya sea que eres principiante o buscas mejorar tus habilidades existentes, nuestros cursos de inglés general proporcionan una base sólida en todos los aspectos del idioma: hablar, escuchar, leer y escribir."
                  : "Whether you're a beginner or looking to improve your existing skills, our general English courses provide a solid foundation in all aspects of the language: speaking, listening, reading, and writing."}
              </p>
              <p className={styles['class-section-text']}>
                {language === "es"
                  ? "Ofrecemos un plan de estudios riguroso que te llevará de principiante a avanzado en tiempo récord. Las clases se ofrecen durante el día y en las tardes para que puedas elegir el horario que mejor se adapte a tu agenda."
                  : "We offer a rigorous curriculum that will take you from beginner to advanced in record time. Classes are offered during the day and in the evenings so you can choose the time that best fits your schedule."}
              </p>
              <p className={styles['class-section-text-bottom']}>
                {language === "es"
                  ? "Nuestro enfoque comunicativo asegura que adquieras habilidades lingüísticas prácticas que puedes usar inmediatamente en situaciones del mundo real, ya sea para viajar, trabajar o crecimiento personal."
                  : "Our communicative approach ensures you gain practical language skills you can use immediately in real-world situations, whether for travel, work, or personal growth."}
              </p>
              <Button
                colorScheme="purple"
                onClick={onContactClick}
              >
                {language === "es" ? "Contacto" : "Contact"}
              </Button>
            </div>
          </div>

          {/* Professors Section */}
          <div className={styles['professors-section']}>
            <h2 className={styles['class-section-title']}>
              {language === "es" ? "Nuestros Profesores: Tus Socios en el Aprendizaje" : "Our Professors: Your Partners in Learning"}
            </h2>
            <p className={styles['class-section-text']}>
              {language === "es"
                ? "¡Nuestros profesores están especialmente capacitados para trabajar con niños de todas las edades! Combinan excelencia académica con un enfoque lúdico y atractivo que mantiene a los jóvenes estudiantes entusiasmados con el inglés. Utilizando juegos, canciones, historias y actividades interactivas, nuestros instructores crean un ambiente dinámico donde los niños aprenden de forma natural y entusiasta."
                : "Our teachers are specially trained to work with children of all ages! They combine academic excellence with a playful, engaging approach that keeps young learners excited about English. Using games, songs, stories, and interactive activities, our instructors create a dynamic environment where children learn naturally and enthusiastically."}
            </p>
            <p className={styles['class-section-text']}>
              {language === "es"
                ? "El aprendizaje debe ser divertido, y nuestros profesores son expertos en hacer que cada lección sea agradable y a la vez educativa. Son pacientes, enérgicos y entienden cómo los niños aprenden mejor en diferentes etapas de desarrollo. Con certificaciones en enseñanza a jóvenes estudiantes y experiencia trabajando con niños, nuestros instructores saben cómo construir confianza y mantener la motivación a través del refuerzo positivo y métodos de enseñanza creativos."
                : "Learning should be fun, and our teachers are experts at making every lesson enjoyable while still being educational. They're patient, energetic, and understand how children learn best at different developmental stages. With certifications in teaching young learners and experience working with kids, our instructors know how to build confidence and maintain motivation through positive reinforcement and creative teaching methods."}
            </p>
            <p className={styles['class-section-text-bottom']}>
              {language === "es"
                ? "Aunque nos especializamos en la enseñanza a niños, nuestros instructores son igualmente hábiles con estudiantes adultos de todos los niveles. Desde principiantes absolutos hasta estudiantes avanzados preparándose para exámenes internacionales de inglés, nuestro versátil personal docente adapta sus métodos para adaptarse a cada grupo de edad y objetivo de aprendizaje. Ya sea que seas un adolescente que quiere mejorar sus calificaciones, un estudiante universitario preparándose para oportunidades internacionales, o un adulto buscando habilidades lingüísticas para crecimiento personal o profesional, nuestros experimentados profesores proporcionan el enfoque y los materiales apropiados para tus necesidades específicas."
                : "While we specialize in teaching children, our instructors are equally skilled with adult learners of all levels. From absolute beginners to advanced students preparing for international English exams, our versatile teaching staff adapts their methods to suit every age group and learning objective. Whether you're a teenager wanting to improve your grades, a university student preparing for international opportunities, or an adult seeking language skills for personal or professional growth, our experienced teachers provide the appropriate approach and materials for your specific needs."}
            </p>
          </div>

          {/* Immersive Spanish Experience */}
          <div className={`${styles['class-item']} ${styles['reverse']}`}>
            <div className={`${styles['class-image']}`}>
              <Image 
                src="/images/icons/immersive-spanish.svg" 
                alt="Immersive Spanish"
                width={250}
                height={280}
                className={`${styles['bouncing-image-slow']} ${styles['class-svg-image-spanish']}`}
              />
            </div>
            <div className={`${styles['class-content']}`}>
              <h2 className={styles['class-section-title']}>
                {language === "es" ? "Experiencia Inmersiva de Español" : "Immersive Spanish Experience"}
              </h2>
              <p className={styles['class-section-text']}>
                {language === "es"
                  ? "¡Experimenta el español en su contexto natural! Nuestro programa inmersivo de español combina el aprendizaje tradicional en el aula con experiencias prácticas en la comunidad local. Aprovecha estar en Perú para aprender español a través de actividades culturales, conversaciones con lugareños y situaciones de la vida real."
                  : "Experience Spanish in its natural context! Our immersive Spanish program combines traditional classroom learning with practical experiences in the local community. Take advantage of being in Peru to learn Spanish through cultural activities, conversations with locals, and real-life situations."}
              </p>
              <p className={styles['class-section-text-bottom']}>
                {language === "es"
                  ? "Este programa es perfecto para visitantes a Oxapampa que quieren aprovechar al máximo su estancia mejorando sus habilidades en español mientras experimentan la cultura local. Disponible para todos los niveles desde principiante hasta avanzado."
                  : "This program is perfect for visitors to Oxapampa who want to make the most of their stay by improving their Spanish skills while experiencing local culture. Available for all levels from beginner to advanced."}
              </p>
              <div className={styles['button-group']}>
                <Button
                  colorScheme="purple"
                  onClick={onContactClick}
                >
                  {language === "es" ? "Contacto" : "Contact"}
                </Button>
                <Button
                  colorScheme="purple"
                  variant="outline"
                  onClick={() => router.push('/courses/immersive-spanish')}
                >
                  {language === "es" ? "Más información" : "More Info"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
